import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import PageHeader from "./layout/PageHeader";
import {
  addSupportTicketComment,
  createSupportTicket,
  fetchSupportTicket,
  fetchSupportTickets,
  getSupportTicketAttachmentUrl,
} from "../utils/api";

const STATUS_STYLES = {
  open: { bg: "#dbeafe", color: "#1d4ed8" },
  in_progress: { bg: "#fef3c7", color: "#b45309" },
  waiting_on_client: { bg: "#fce7f3", color: "#be185d" },
  resolved: { bg: "#dcfce7", color: "#15803d" },
  closed: { bg: "#e2e8f0", color: "#475569" },
};

const STATUS_LABELS = {
  open: "Open",
  in_progress: "In Progress",
  waiting_on_client: "Waiting on You",
  resolved: "Resolved",
  closed: "Closed",
};

const formatDateTime = (value) => {
  if (!value) return "--";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "--";
  return date.toLocaleString();
};

const StatusBadge = ({ status }) => {
  const style = STATUS_STYLES[status] || STATUS_STYLES.open;
  return (
    <span
      style={{
        display: "inline-block",
        padding: "2px 10px",
        borderRadius: 999,
        fontSize: 12,
        fontWeight: 600,
        background: style.bg,
        color: style.color,
      }}
    >
      {STATUS_LABELS[status] || status}
    </span>
  );
};

const SupportTickets = (props) => {
  const [searchParams] = useSearchParams();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [selectedId, setSelectedId] = useState("");
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [subject, setSubject] = useState("");
  const [description, setDescription] = useState("");
  const [screenshots, setScreenshots] = useState([]);
  const [commentText, setCommentText] = useState("");
  const [commentSaving, setCommentSaving] = useState(false);

  useEffect(() => {
    const ticketFromUrl = String(searchParams.get("ticket") || "").trim();
    if (ticketFromUrl) {
      setSelectedId(ticketFromUrl);
    }
  }, [searchParams]);

  const loadTickets = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await fetchSupportTickets();
      setTickets(Array.isArray(data?.tickets) ? data.tickets : []);
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to load support tickets.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadTickets();
  }, [loadTickets]);

  const loadDetail = useCallback(async (ticketId) => {
    if (!ticketId) return;
    setDetailLoading(true);
    try {
      const data = await fetchSupportTicket(ticketId);
      setSelectedTicket(data?.ticket || null);
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to load ticket details.");
    } finally {
      setDetailLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedId) {
      void loadDetail(selectedId);
    } else {
      setSelectedTicket(null);
    }
  }, [selectedId, loadDetail]);

  const openCount = useMemo(
    () => tickets.filter((t) => !["resolved", "closed"].includes(t.status)).length,
    [tickets],
  );

  const handleCreate = async (event) => {
    event.preventDefault();
    setCreating(true);
    setError("");
    setSuccess("");
    try {
      await createSupportTicket({ subject, description, screenshots });
      setSubject("");
      setDescription("");
      setScreenshots([]);
      setShowForm(false);
      setSuccess("Support ticket submitted. Our team will respond soon.");
      await loadTickets();
    } catch (err) {
      setError(err?.message || err?.response?.data?.message || "Failed to submit ticket.");
    } finally {
      setCreating(false);
    }
  };

  const handleAddComment = async () => {
    if (!selectedId || !commentText.trim()) return;
    setCommentSaving(true);
    setError("");
    try {
      const data = await addSupportTicketComment(selectedId, commentText.trim());
      setSelectedTicket(data?.ticket || null);
      setCommentText("");
      await loadTickets();
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to send reply.");
    } finally {
      setCommentSaving(false);
    }
  };

  const openAttachment = async (filename) => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(getSupportTicketAttachmentUrl(filename), {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!response.ok) throw new Error("Unable to open attachment");
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener,noreferrer");
    } catch {
      setError("Unable to open screenshot.");
    }
  };

  return (
    <div className="page-shell ai-hiring-page">
      <PageHeader
        title="Support Tickets"
        subtitle="Report a tech issue, attach screenshots, and track resolution."
        icon={<span role="img" aria-label="Support">🎫</span>}
        {...props}
      />
      <div className="ai-hiring-content">
        {success ? <div className="ai-alert ai-alert-success">{success}</div> : null}
        {error ? <div className="ai-alert ai-alert-error">{error}</div> : null}

        <div className="ai-settings-grid" style={{ marginBottom: 16 }}>
          <div className="ai-card ai-settings-card">
            <p className="ai-card-note">Open Tickets</p>
            <h3 className="ai-card-title">{openCount}</h3>
          </div>
          <div className="ai-card ai-settings-card">
            <p className="ai-card-note">Total Submitted</p>
            <h3 className="ai-card-title">{tickets.length}</h3>
          </div>
        </div>

        <div className="ai-card" style={{ marginBottom: 16 }}>
          <div className="ai-card-header">
            <div>
              <p className="ai-card-note">MyAlbertAI Support</p>
              <h3 className="ai-card-title">Create a Ticket</h3>
            </div>
            <button
              type="button"
              className="ai-button ai-button-primary ai-button-sm"
              onClick={() => setShowForm((prev) => !prev)}
            >
              {showForm ? "Cancel" : "New Ticket"}
            </button>
          </div>
          {showForm ? (
            <form onSubmit={handleCreate} style={{ display: "grid", gap: 12 }}>
              <div>
                <label className="ai-card-note">Subject</label>
                <input
                  className="ai-input"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="Brief summary of the issue"
                  maxLength={200}
                  required
                />
              </div>
              <div>
                <label className="ai-card-note">Description</label>
                <textarea
                  className="ai-input"
                  rows={5}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="What happened? What were you trying to do? Include steps if possible."
                  required
                />
              </div>
              <div>
                <label className="ai-card-note">Screenshots (optional, up to 5)</label>
                <input
                  className="ai-input"
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  multiple
                  onChange={(e) => setScreenshots(Array.from(e.target.files || []).slice(0, 5))}
                />
                {screenshots.length ? (
                  <small className="ai-card-note">{screenshots.length} file(s) selected</small>
                ) : null}
              </div>
              <div>
                <button
                  type="submit"
                  className="ai-button ai-button-primary"
                  disabled={creating}
                >
                  {creating ? "Submitting..." : "Submit Ticket"}
                </button>
              </div>
            </form>
          ) : (
            <p className="ai-card-note">
              Click <strong>New Ticket</strong> to report a technical issue with MyAlbertAI.
            </p>
          )}
        </div>

        <div className="ai-card">
          <div className="ai-card-header">
            <div>
              <p className="ai-card-note">Tracking</p>
              <h3 className="ai-card-title">Your Ticket History</h3>
            </div>
          </div>
          {loading ? (
            <p className="ai-card-note">Loading tickets...</p>
          ) : tickets.length === 0 ? (
            <p className="ai-card-note">No support tickets yet.</p>
          ) : (
            <div className="ai-table-wrapper">
              <table className="ai-table">
                <thead>
                  <tr>
                    <th>Ticket #</th>
                    <th>Subject</th>
                    <th>Status</th>
                    <th>Submitted By</th>
                    <th>Date</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {tickets.map((ticket) => (
                    <tr key={ticket._id || ticket.id}>
                      <td>{ticket.ticketNumber}</td>
                      <td>{ticket.subject}</td>
                      <td>
                        <StatusBadge status={ticket.status} />
                      </td>
                      <td>{ticket.createdBy?.name || ticket.createdBy?.email || "--"}</td>
                      <td>{formatDateTime(ticket.createdAt)}</td>
                      <td>
                        <button
                          type="button"
                          className="ai-button ai-button-sm"
                          onClick={() => setSelectedId(String(ticket._id || ticket.id))}
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {selectedId ? (
          <div className="ai-card" style={{ marginTop: 16 }}>
            <div className="ai-card-header">
              <div>
                <p className="ai-card-note">Ticket Detail</p>
                <h3 className="ai-card-title">
                  {selectedTicket?.ticketNumber || "Loading..."}
                </h3>
              </div>
              <button
                type="button"
                className="ai-button ai-button-sm"
                onClick={() => setSelectedId("")}
              >
                Close
              </button>
            </div>
            {detailLoading || !selectedTicket ? (
              <p className="ai-card-note">Loading ticket details...</p>
            ) : (
              <div style={{ display: "grid", gap: 16 }}>
                <div>
                  <strong>{selectedTicket.subject}</strong>
                  <div style={{ marginTop: 8 }}>
                    <StatusBadge status={selectedTicket.status} />
                  </div>
                </div>
                <div>
                  <p className="ai-card-note">Description</p>
                  <div style={{ whiteSpace: "pre-wrap" }}>{selectedTicket.description}</div>
                </div>
                <div className="ai-settings-grid">
                  <div>
                    <p className="ai-card-note">Submitted By</p>
                    <div>{selectedTicket.createdBy?.name || "--"}</div>
                    <div style={{ color: "#64748b", fontSize: 13 }}>
                      {selectedTicket.createdBy?.email || ""}
                    </div>
                  </div>
                  <div>
                    <p className="ai-card-note">Location</p>
                    <div>{selectedTicket.locationName || "--"}</div>
                  </div>
                  <div>
                    <p className="ai-card-note">Submitted</p>
                    <div>{formatDateTime(selectedTicket.createdAt)}</div>
                  </div>
                  <div>
                    <p className="ai-card-note">Assigned To</p>
                    <div>{selectedTicket.assignedTo?.name || "Not yet assigned"}</div>
                  </div>
                </div>
                {selectedTicket.attachments?.length ? (
                  <div>
                    <p className="ai-card-note">Screenshots</p>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                      {selectedTicket.attachments.map((file) => (
                        <button
                          key={file.filename}
                          type="button"
                          className="ai-button ai-button-sm"
                          onClick={() => openAttachment(file.filename)}
                        >
                          {file.originalName || file.filename}
                        </button>
                      ))}
                    </div>
                  </div>
                ) : null}
                <div>
                  <p className="ai-card-note">Status History</p>
                  <div style={{ display: "grid", gap: 8 }}>
                    {(selectedTicket.statusHistory || [])
                      .slice()
                      .reverse()
                      .map((entry, idx) => (
                        <div
                          key={`${entry.changedAt}-${idx}`}
                          style={{
                            padding: 10,
                            borderRadius: 8,
                            background: "#f8fafc",
                            border: "1px solid #e2e8f0",
                          }}
                        >
                          <div style={{ fontWeight: 600 }}>
                            {STATUS_LABELS[entry.status] || entry.status}
                          </div>
                          <div style={{ fontSize: 13, color: "#64748b" }}>
                            {formatDateTime(entry.changedAt)} ·{" "}
                            {entry.changedBy?.name || "System"}
                          </div>
                          {entry.note ? <div style={{ marginTop: 4 }}>{entry.note}</div> : null}
                        </div>
                      ))}
                  </div>
                </div>
                <div>
                  <p className="ai-card-note">Conversation</p>
                  <div style={{ display: "grid", gap: 8, marginBottom: 12 }}>
                    {(selectedTicket.comments || []).map((comment, idx) => (
                      <div
                        key={`${comment.createdAt}-${idx}`}
                        style={{
                          padding: 10,
                          borderRadius: 8,
                          background: comment.isStaff ? "#eff6ff" : "#f8fafc",
                          border: "1px solid #e2e8f0",
                        }}
                      >
                        <div style={{ fontWeight: 600 }}>
                          {comment.author?.name || "User"}
                          {comment.isStaff ? " (Support Team)" : ""}
                        </div>
                        <div style={{ fontSize: 13, color: "#64748b" }}>
                          {formatDateTime(comment.createdAt)}
                        </div>
                        <div style={{ marginTop: 4, whiteSpace: "pre-wrap" }}>{comment.text}</div>
                      </div>
                    ))}
                  </div>
                  {selectedTicket.status !== "closed" ? (
                    <div style={{ display: "grid", gap: 8 }}>
                      <textarea
                        className="ai-input"
                        rows={3}
                        value={commentText}
                        onChange={(e) => setCommentText(e.target.value)}
                        placeholder="Add a reply or more details..."
                      />
                      <button
                        type="button"
                        className="ai-button ai-button-primary ai-button-sm"
                        disabled={commentSaving || !commentText.trim()}
                        onClick={handleAddComment}
                      >
                        {commentSaving ? "Sending..." : "Send Reply"}
                      </button>
                    </div>
                  ) : null}
                </div>
              </div>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default SupportTickets;
